import { Alert } from '@ordo/ui/alert'
import { cn } from '@ordo/ui/cn'
import { ArrowRight, X } from 'lucide-react'

import { Badge } from '@/components/chefs/badge'
import { Button } from '@/components/chefs/button'
import { CHIP_CLASS_LG_PRIMARY, ChipButton } from '@/components/chefs/chip-button'
import type { Substitution } from '@/lib/chefs/substitutions'

interface LedgerProps {
  substitutions: Substitution[]
  showSubmittedConfirmation: boolean
  onDismissConfirmation: () => void
  onRequestNew: () => void
}

const STATUS_VARIANT: Record<Substitution['status'], { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700' },
  approved: { label: 'Approved', className: 'bg-green-50 text-green-800' },
  rejected: { label: 'Rejected', className: 'bg-rose-100 text-rose-700' },
}

function formatRange(startIso: string, endIso: string): string {
  const start = new Date(`${startIso}T00:00:00`)
  const end = new Date(`${endIso}T00:00:00`)
  const sameYear = start.getFullYear() === end.getFullYear()
  const sameMonth = sameYear && start.getMonth() === end.getMonth()
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' })
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' })
  if (sameMonth) {
    return `${startMonth} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`
  }
  if (sameYear) {
    return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${end.getFullYear()}`
  }
  return `${startMonth} ${start.getDate()}, ${start.getFullYear()} – ${endMonth} ${end.getDate()}, ${end.getFullYear()}`
}

export function SubstitutionModalLedger({
  substitutions,
  showSubmittedConfirmation,
  onDismissConfirmation,
  onRequestNew,
}: LedgerProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {showSubmittedConfirmation ? (
        <Alert variant="success" className="flex shrink-0 items-start gap-3">
          <div className="flex-1">
            <div className="font-medium">Substitution request submitted</div>
            <div className="text-sm opacity-90">
              Our team will review the request and get back to you within 24 hours.
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Dismiss"
            className="text-muted-foreground"
            onClick={onDismissConfirmation}>
            <X className="h-4 w-4" />
          </Button>
        </Alert>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {substitutions.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1.5 rounded-md border border-border px-6 py-12 text-center">
            <p className="text-sm font-medium text-foreground">No substitutions yet</p>
            <p className="text-xs text-muted-foreground">
              Request a substitution to replace an ingredient for a date range.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Original</th>
                  <th className="px-3 py-2">Replacement</th>
                  <th className="px-3 py-2">Dates</th>
                  <th className="w-28 px-3 py-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {substitutions.map((sub) => {
                  const status = STATUS_VARIANT[sub.status]
                  return (
                    <tr key={sub.id}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-foreground">{sub.ingredientName}</div>
                        <div className="text-xs text-muted-foreground">{sub.ingredientUnit}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 text-foreground">
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                          {sub.replacement}
                        </div>
                        {sub.rejectionReason ? (
                          <div className="mt-0.5 text-xs text-muted-foreground">{sub.rejectionReason}</div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-foreground">
                        {formatRange(sub.startDate, sub.endDate)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Badge className={cn('rounded-full px-2 py-0.5 text-[11px]', status.className)}>
                          {status.label}
                        </Badge>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex shrink-0 justify-end pt-1">
        <ChipButton variant="default" className={CHIP_CLASS_LG_PRIMARY} onClick={onRequestNew}>
          Request new substitution
        </ChipButton>
      </div>
    </div>
  )
}
